import api from '@/lib/api';

const RETRIES = 3;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const isTransient = (err) => !err.response || [429, 502, 503, 504].includes(err.response.status);

async function withRetry(fn) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= RETRIES || !isTransient(err)) throw err;
      await sleep(1500 * (attempt + 1));
    }
  }
}

// Sends the file in server-sized chunks; resolves with the processing job_id
export async function uploadInChunks(file, mode, onProgress) {
  const { data } = await withRetry(() => api.post('/upload/init', { filename: file.name, size: file.size, mode }));
  const { job_id: jobId, chunk_size: chunkSize } = data;
  let sent = 0;
  for (let index = 0; sent < file.size; index += 1) {
    const blob = file.slice(sent, sent + chunkSize);
    const form = new FormData();
    form.append('chunk', blob, file.name);
    await withRetry(() => api.post(`/upload/chunk/${jobId}?index=${index}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }));
    sent += blob.size;
    onProgress(sent, file.size);
  }
  try {
    await withRetry(() => api.post(`/upload/complete/${jobId}`));
  } catch (err) {
    // A retried complete may land after the first one already started processing
    if (err.response?.status !== 409) throw err;
  }
  return jobId;
}

export function describeUploadError(err) {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  const status = err.response?.status;
  if (status === 413) return 'File is too large — the upload limit is 50 MB.';
  if (status === 403) return 'Your role does not permit this upload.';
  if (status === 429) return 'Too many requests — please wait a minute and retry.';
  if ([502, 503, 504].includes(status)) return 'The server is busy right now — please retry in a minute. Your existing data was not changed.';
  if (!err.response) return 'Connection lost during upload — check your network and retry. Your existing data was not changed.';
  return `Upload failed (error ${status}).`;
}
